// =====================================================================
// RechercheColis.jsx — M-008 — recherche colis + photo + détails
// =====================================================================
// Reproduit fidèlement le panneau validé (zeus-search.js) en natif React,
// au look Zeus Log. Sépare clairement :
//   📦 Contrôle packing (Effitrace) = qui a emballé/contrôlé + quand
//   🏷️ Scan convoyeur               = passage physique réel sur le tapis
// =====================================================================

import { useState, useEffect, useCallback, useRef } from 'react';
import { convoyeur as convoyeurApi } from '../../services/api.js';
import {
  inputBase, btnPrimary, CarrierChip, fmtWeight, fmtDims, isOfflineError,
} from './styles.jsx';

const SEARCH_BY = [
  { v: 'noSuivi',         l: 'N° suivi (T&T)' },
  { v: 'commande',        l: 'Commande' },
  { v: 'commandeExterne', l: 'Commande externe' },
  { v: 'odp',             l: 'ODP' },
];

// placeholder code-barres (si pas de photo)
function placeholder(bc) {
  const bars = [26, 31, 35, 41, 47, 53, 59, 65, 71, 77, 83, 89]
    .map(x => `<rect x="${x}" y="24" width="2" height="26" fill="#714B67"/>`).join('');
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="140" height="100"><rect width="140" height="100" rx="8" fill="#f3effa"/><rect x="32" y="26" width="76" height="34" rx="3" fill="#fff" stroke="#cbd5e1"/>${bars}<text x="70" y="80" font-size="8" text-anchor="middle" fill="#64748b" font-family="monospace">${String(bc || '').slice(0, 20)}</text></svg>`;
  return 'data:image/svg+xml;utf8,' + encodeURIComponent(svg);
}

export default function RechercheColis() {
  const [by, setBy] = useState('noSuivi');
  const [q, setQ] = useState('');
  const [results, setResults] = useState(null); // null = pas encore cherché
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [carrierLine, setCarrierLine] = useState({}); // carrier -> { lineNo, gravNo } (ligne de tri prévue)
  const [lightbox, setLightbox] = useState(null); // { src, label } quand une photo est agrandie
  const inputRef = useRef(null);

  // carte carrier -> ligne de tri prévue (depuis line-stats)
  useEffect(() => {
    convoyeurApi.lineStats().then(d => {
      const lines = (d.lines || []).slice().sort((a, b) => a.line - b.line);
      const map = {};
      lines.forEach((l, i) => {
        (l.carriers || [l.carrier]).forEach(c => { if (c && !(c in map)) map[c] = { lineNo: i + 1, gravNo: l.line }; });
      });
      setCarrierLine(map);
    }).catch(() => setCarrierLine({}));
  }, []);

  const doSearch = useCallback(async () => {
    const term = q.trim();
    if (!term) { setResults(null); return; }
    setLoading(true); setError(null);
    try {
      const list = await convoyeurApi.search(by, term);
      setResults(Array.isArray(list) ? list : []);
    } catch (err) {
      setError(isOfflineError(err) ? 'Convoyeur hors-ligne — recherche indisponible pour le moment.' : 'Erreur lors de la recherche.');
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [by, q]);

  return (
    <div>
      {/* Barre de recherche */}
      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '1rem' }}>
        <select value={by} onChange={e => setBy(e.target.value)} style={{ ...inputBase, minWidth: 160 }}>
          {SEARCH_BY.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
        </select>
        <input
          ref={inputRef}
          type="text"
          placeholder="Introduire la valeur…"
          value={q}
          onChange={e => setQ(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') doSearch(); }}
          style={{ ...inputBase, flex: '1 1 240px' }}
        />
        <button style={btnPrimary} onClick={doSearch} disabled={loading}>{loading ? '…' : 'Rechercher'}</button>
      </div>

      {error && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '0.75rem 1rem', borderRadius: 4, marginBottom: '1rem' }}>{error}</div>}

      {results === null && !loading && (
        <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af', background: '#f9fafb', borderRadius: 8 }}>
          Recherchez un colis par N° suivi (T&amp;T), commande ou ODP.
        </div>
      )}

      {results && results.length === 0 && !loading && !error && (
        <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af', background: '#f9fafb', borderRadius: 8 }}>
          Aucun colis trouvé pour « {q.trim()} ».
        </div>
      )}

      {results && results.length > 0 && (
        <>
          <div style={{ color: '#6b7280', fontSize: '0.8125rem', margin: '0 0 0.75rem' }}>{results.length} colis trouvé{results.length > 1 ? 's' : ''}</div>
          {results.map((p, i) => <ColisCard key={p.noSuivi || p.commande || i} p={p} prevue={carrierLine[p.carrierFamily]} onPhotoClick={setLightbox} />)}
        </>
      )}

      {/* Lightbox : photo agrandie au clic */}
      {lightbox && (
        <div
          onClick={() => setLightbox(null)}
          style={{ position: 'fixed', inset: 0, zIndex: 1100, background: 'rgba(15,23,42,.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '4vh 4vw', cursor: 'zoom-out' }}
        >
          <div style={{ position: 'relative', maxWidth: '92vw', maxHeight: '92vh' }} onClick={(e) => e.stopPropagation()}>
            <img src={lightbox.src} alt={lightbox.label || 'colis'} style={{ maxWidth: '92vw', maxHeight: '86vh', objectFit: 'contain', borderRadius: 8, boxShadow: '0 20px 60px rgba(0,0,0,.5)', background: '#fff' }} />
            {lightbox.label && <div style={{ color: '#fff', textAlign: 'center', marginTop: 8, fontFamily: 'monospace', fontSize: '0.875rem' }}>{lightbox.label}</div>}
            <button onClick={() => setLightbox(null)} style={{ position: 'absolute', top: -12, right: -12, width: 34, height: 34, borderRadius: 999, border: 0, background: '#fff', color: '#1e293b', fontSize: 18, fontWeight: 700, cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,.3)' }}>✕</button>
          </div>
        </div>
      )}
    </div>
  );
}

function ColisCard({ p, prevue, onPhotoClick }) {
  const bc = (p.barcodes && p.barcodes[0]) || p.noSuivi || '';
  const sc = !!p.scanned;
  const articles = p.articles || [];
  const realSrc = convoyeurApi.imageUrl(bc);
  const [photoState, setPhotoState] = useState('loading'); // 'loading' | 'ok' | 'placeholder'
  const hasPhoto = photoState === 'ok';

  return (
    <div style={{
      display: 'flex', gap: 16, flexWrap: 'wrap',
      border: '1px solid #e5e7eb', borderRadius: 12, padding: 14, marginBottom: 12,
      background: '#fff',
    }}>
      {/* Photo (clic = agrandir si photo réelle dispo) */}
      <img
        src={realSrc}
        onLoad={() => setPhotoState(s => s === 'placeholder' ? 'placeholder' : 'ok')}
        onError={(e) => { e.currentTarget.onerror = null; setPhotoState('placeholder'); e.currentTarget.src = placeholder(bc); }}
        onClick={() => { if (hasPhoto) onPhotoClick({ src: realSrc, label: p.noSuivi || bc }); }}
        alt="colis"
        title={hasPhoto ? 'Cliquer pour agrandir' : 'Photo pas encore disponible'}
        style={{ width: 160, height: 112, objectFit: 'cover', borderRadius: 8, border: '1px solid #e5e7eb', background: '#f3effa', cursor: hasPhoto ? 'zoom-in' : 'default' }}
      />

      {/* Détails */}
      <div style={{ flex: '1 1 320px', minWidth: 260 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>{p.commande || p.commandeExterne || p.odp || '—'}</span>
          <CarrierChip carrier={p.carrierFamily} small />
          {prevue && (
            <span title="Ligne de tri prévue (selon transporteur)" style={{ fontSize: '0.6875rem', color: '#475569', background: '#f1f5f9', borderRadius: 6, padding: '2px 7px', fontWeight: 600 }}>
              → L{prevue.lineNo} · {prevue.gravNo}
            </span>
          )}
          <span style={{
            fontWeight: 700, fontSize: '0.6875rem', padding: '2px 8px', borderRadius: 6,
            ...(sc ? { color: '#15803d', background: '#dcfce7' } : { color: '#b45309', background: '#fef3c7' }),
          }}>{sc ? '✅ Contrôlé' : '⏳ À contrôler'}</span>
        </div>

        <div style={{ fontSize: '0.75rem', color: '#64748b', margin: '4px 0' }}>
          ODP: {p.odp || '—'} · Externe: {p.commandeExterne || '—'} · Client: <strong style={{ color: '#374151' }}>{p.client || '—'}</strong>
          {p.partner && <> · Partenaire: <strong style={{ color: '#374151' }}>{p.partner}</strong></>}
        </div>

        <div style={{ fontFamily: 'monospace', fontSize: '0.8125rem', color: '#1e293b' }}>
          T&amp;T: <strong>{p.noSuivi || '—'}</strong>
        </div>

        {/* (1) Contrôle packing (Effitrace) */}
        {sc ? (
          <div style={{ fontSize: '0.75rem', color: '#1e293b', marginTop: 4 }}>
            📦 <strong>Contrôle packing</strong> (Effitrace) : le {p.dateControle || '—'} à <strong>{p.heureControle || '—'}</strong>
            {p.controlePar && <> · par <strong>{p.controlePar}</strong></>}
          </div>
        ) : (
          <div style={{ fontSize: '0.75rem', color: '#b45309', marginTop: 4 }}>
            📦 <strong>Contrôle packing</strong> : <strong>À contrôler</strong> (pas encore emballé)
          </div>
        )}

        {/* (2) Scan convoyeur (passage physique réel sur le tapis) */}
        {p.convScanAt ? (
          <div style={{ fontSize: '0.75rem', color: '#1e293b' }}>
            🏷️ <strong>Scan convoyeur</strong> : à <strong>{p.convScanAt}</strong>{p.convLine && <> · ligne {p.convLine}</>}
          </div>
        ) : (
          <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
            🏷️ <strong>Scan convoyeur</strong> : <em>en attente (pas encore passé sur le tapis)</em>
          </div>
        )}

        <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: 2 }}>
          Poids : <strong style={{ color: '#374151' }}>{fmtWeight(p.weight_g)}</strong> · Dim : {fmtDims([p.L_cm, p.l_cm, p.h_cm])}
          {p.rangColis != null && <> · Colis {p.rangColis}</>}
        </div>

        {articles.length > 0 && (
          <div style={{ marginTop: 6, borderTop: '1px solid #e5e7eb', paddingTop: 6 }}>
            {articles.map((a, i) => (
              <div key={i} style={{ fontSize: '0.75rem', color: '#64748b' }}>
                • {a.qte || ''}× {a.des || a.article || ''} <span style={{ color: '#94a3b8' }}>{a.ean || ''}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
